<template>
  <b-modal
    ref="topupModal"
    modal-class="topup-modal"
    centered
    hide-header-close
    :size="size"
    :title="title"
    @hidden="finishWorking" >
    <ValidationObserver
      ref="customFieldObserver"
      v-slot="{ valid, dirty }" >
      <h4>Amount</h4>
      <ValidationProvider
        v-slot="{ errors }"
        :rules="{
          number: {
            message: `Invalid format. Enter a whole-dollar amount in ${targetCurrency.code} (e.g., ${targetCurrency.symbol}123).`,
          },
          minNumber: {
            min: 1,
            message: `Amount must be at least ${targetCurrency.symbol}1.`,
          },
          maxNumber: {
            max: 5000,
            message: `Amount must be lower than ${targetCurrency.symbol}5,000.`,
          },
          noDecimals: {
            message: `Invalid format. Enter a whole-dollar amount in ${targetCurrency.code} (e.g., ${targetCurrency.symbol}123).`,
          },
          required: true,
        }"
        name="topupAmount" >
        <PrefixInput
          v-model="topupAmount"
          placeholder="10"
          :prefix="targetCurrency.symbol" />
        <div
          v-if="errors[0]"
          class="validation-error flex flex-align-center">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.ERROR"
            :color="SBColors.redPrimary"
            :size="12" />
          <span>{{ errors[0] }}</span>
        </div>
      </ValidationProvider>
    </ValidationObserver>
    <template slot="modal-footer">
      <div class="modal-footer__message">
        <div
          v-if="showUndoWarning"
          class="modal-footer__undo-warning flex flex-align-center">
          <span class="sb2-icon icon-error size-12"/>This action cannot be undone.
        </div>
      </div>
      <div class="modal-footer__buttons flex flex-row flex-nowrap">
        <b-button @click="hide">{{ cancelButtonText }}</b-button>
        <b-button
          :variant="buttonVariant"
          class="btn-primary"
          :class="{loading: working}"
          :disabled="!customFieldObserver.dirty || !customFieldObserver.valid"
          @click="confirm" >
          <span>{{ confirmButtonText }}</span>
          <Spinner
            v-if="working"
            :size="14"
            :thickness="2"
            :color="SBColors.white" />
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from '@/components/common/Spinner';
import PrefixInput from '@/components/common/PrefixInput';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import api from '@/helpers/api';
import Currencies from '@/helpers/lists/currencies';

export default {
  name: 'TopupModal',
  components: {
    Spinner,
    PrefixInput,
    SBIcon,
  },
  props: {
    currency: {
      type: String,
      default: 'USD',
      required: false,
    },
  },
  data() {
    return {
      topupAmount: null,
      confirmButtonText: 'Add',
      cancelButtonText: 'Cancel',
      title: 'Add funds',
      buttonVariant: 'primary',
      size: '',
      showUndoWarning: false,
      autoClose: false, // TODO this needs to be fixed to accept true, false. For now just write NO or something.
      working: false,
      onConfirmCallback() {},
      SBColors,
      SBIconList,
      customFieldObserver: {
        dirty: false,
        valid: false,
      },
      resetAmount: false,
      Currencies,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      paymentUpdate: 'organisation/paymentUpdate',
    }),
    targetCurrency() {
      const targetCurrency = Currencies.find(currency => currency.code === this.currency);
      if (targetCurrency) return targetCurrency;
      return Currencies.find(currency => currency.code === 'USD');
    },
  },
  watch: {
    topupAmount() {
      if (this.$refs.customFieldObserver) {
        this.$refs.customFieldObserver.validate()
          .then(() => {
            this.$set(this, 'customFieldObserver', this.$refs.customFieldObserver.flags);
          });
      }
    },
    paymentUpdate(newVal) {
      if (newVal.subject === 'spending_funds_topup') {
        if (newVal.action === 'payment_failed') {
          this.$emit('status', false);
          this.hide();
        } else {
          this.$emit('status', true);
          this.hide();
          this.resetAmount = true;
        }
      }
    },
  },
  methods: {
    /**
     * Shows the modal to the user.
     *
     * @param params Object which expects title, confirmButtonText, cancelButtonText, autoClose.
     */
    show(params = {}) {
      if (params.title) this.title = params.title;
      if (params.confirmButtonText) this.confirmButtonText = params.confirmButtonText;
      if (params.cancelButtonText) this.cancelButtonText = params.cancelButtonText;
      this.autoClose = params.autoClose || false;
      this.$refs.topupModal.show();
    },
    hide() {
      this.$refs.topupModal.hide();
    },
    finishWorking() {
      this.working = false;
      if (this.resetAmount) {
        this.topupAmount = null;
      }
    },
    confirm() {
      this.working = true;

      api('POST', `/organisation/${this.currentOrganisation.id}/spending/manual-topup`, { topup_amount: Number(this.topupAmount), topup_currency: this.currency }, null, 'w')
        .then((response) => {
          if (response.status !== 200) {
            this.$emit('status', false);
            this.hide();
          }
        })
        .catch(() => {
          this.$emit('status', false);
          this.hide();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .topup-modal {
  .modal-dialog {
    max-width: 422px;
  }

  .modal-content {
    .modal-header {
      padding: 20px;
      box-shadow: none;
      border-bottom: solid 1px $greyBorderModal;

      .modal-title {
        font-size: 16px;
        font-weight: 500;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.2;
        letter-spacing: normal;
        color: $blackPrimary;
      }
    }
    .modal-body {
      padding: 20px;
      font-size: 14px;
      font-weight: normal;
      line-height: 1.2;
      color: $blackPrimary;
      background-color: $greyUltralight;

      h4 {
        font-size: 14px;
        font-weight: 500;
        line-height: 1.2;
        margin-bottom: 6px;
      }
    }
    .modal-footer {
      border-top: solid 1px $greyBorderModal;
      align-items: center;
      // justify-content: space-between;
      // flex-direction: row;
    }
    .modal-footer>* {
      margin: 0;
    }
    .modal-footer__message {
      // style if needed
    }
    .modal-footer__buttons {
      // style if needed
      .btn.loading {
        position: relative;

        span {
          visibility: hidden;
        }

        .loader {
          position: absolute;
          height: 100%;
          display: flex;
          align-items: center;
        }
      }
    }
    .modal-footer__undo-warning {
      color: $redPrimary;
      font-size: 14px;
      font-weight: 400;
      // icon 4px from text
      .sb2-icon {
        margin-right: 4px;
      }
    }
  }
}
</style>
